/**
 * Статусы загрузки фото на Я.Маркет и нейтральная заглушка вместо битой
 * иконки — решение владельца (SPEC_2026-09-13_YANDEX_MARKET.md, §4.2 и
 * задача 2F1, п.1).
 *
 * Факт: API Маркета не отдаёт адреса своих копий картинок — `pictures` это
 * ссылки продавца (у нас — легаси `printort.ru/wp-content/...`), многие из
 * которых мертвы (404), хотя Маркет картинку загрузил. Показываем ссылку как
 * есть плюс статус загрузки; визуалы ПИМ как замену НЕ подставляем.
 *
 * `ImagePreviewThumb` (components/ui) не сообщает наружу, что картинка не
 * загрузилась в браузере — при ошибке он сам рисует красную «битую» иконку
 * (`ImagePreviewError`), а нам нужна нейтральная заглушка с текстом, который
 * зависит от статуса площадки. Поэтому здесь — свой лёгкий wrapper поверх
 * `AuthorizedMediaImage` (`shared/media/authorizedMedia`, не `components/ui`),
 * у которого `errorFallback` можно переопределить.
 */

import { useEffect, useState } from 'react'
import { ImageOff } from 'lucide-react'
import type { BadgeVariant } from '../../../components/ui/Badge'
import { ImagePreviewThumb } from '../../../components/ui/ImagePreviewThumb'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'

/** Известные значения `uploadState` Партнёрского API (проверено — только `UPLOADED`; остальные — по документации). */
export const YM_PHOTO_STATUS_LABELS: Record<string, { label: string; tone: BadgeVariant }> = {
  UPLOADED: { label: 'Загружено на Маркет', tone: 'green' },
  IN_PROCESS: { label: 'Обрабатывается Маркетом', tone: 'yellow' },
  UPLOADING: { label: 'Загружается на Маркет', tone: 'yellow' },
  FAILED: { label: 'Маркет не принял фото', tone: 'red' },
}

/** Подпись бейджа статуса загрузки; незнакомый код — как есть, серым, без падения. */
export const ymPhotoStatusLabel = (state?: string | null): string => {
  if (!state) return 'Статус неизвестен'
  return YM_PHOTO_STATUS_LABELS[state]?.label || state
}

export const ymPhotoStatusVariant = (state?: string | null): BadgeVariant =>
  (state && YM_PHOTO_STATUS_LABELS[state]?.tone) || 'gray'

/**
 * Подсказка для плитки/превью, когда картинка не загрузилась в браузере.
 * Решение владельца: `UPLOADED` → «источник удалён, на Маркете копия есть»;
 * другой статус → отдельная формулировка со статусом; `null` → просто
 * «недоступна».
 */
export const ymPhotoUnavailableHint = (state?: string | null): string => {
  if (state === 'UPLOADED') return 'Источник удалён, на Маркете копия есть'
  if (state) return `Картинка недоступна и не загружена на Маркет (${ymPhotoStatusLabel(state)})`
  return 'Картинка недоступна'
}

/** Нейтральная нижняя плашка вместо красной «битой» иконки components/ui. */
function YmPhotoUnavailable({ size, hint }: { size: string; hint: string }) {
  return (
    <span
      role="img"
      aria-label={hint}
      title={hint}
      className={`${size} flex shrink-0 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-gray-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500`}
    >
      <ImageOff className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  )
}

const DEFAULT_SIZE = 'h-11 w-8'

export interface YmPhotoThumbProps {
  /** Ссылка карточки как есть (может быть мёртвой — площадка своих копий не отдаёт). */
  src: string | null | undefined
  /** Статус загрузки ЭТОЙ картинки на Маркете (main_picture_state либо picture_states[i].upload_state). */
  uploadState?: string | null
  alt?: string
  size?: string
  thumbnailWidth?: number
  /** Картинка не загрузилась в браузере — нужно потребителям, показывающим отдельную пометку рядом (модалка). */
  onLoadError?: () => void
  /**
   * Увеличение при наведении, как у превью ВБ/ОЗОН (`components/ui/ImagePreviewThumb`).
   * Нужно в списках, где плитка мелкая; в карточке товара кадр и так крупный.
   */
  zoom?: boolean
}

/**
 * Плитка/колонка фото каталога и модалки — с нейтральной заглушкой при ошибке
 * загрузки.
 *
 * Ссылки карточки — внешние (хост продавца), а не наш гейтвей, поэтому
 * `AuthorizedMediaImage` отдаёт их браузеру как обычный `<img src>` без
 * авторизованной загрузки — его `errorFallback` реагирует только на отказ
 * `fetch` (внутренние адреса), а не на «404 тела картинки» у внешнего хоста.
 * Реальный промах внешней ссылки ловит нативный `onError` тега — свой `failed`
 * по тому же приёму, что у `components/ui/ImagePreviewThumb`.
 */
export function YmPhotoThumb({
  src,
  uploadState = null,
  alt = '',
  size = DEFAULT_SIZE,
  thumbnailWidth,
  onLoadError,
  zoom = false,
}: YmPhotoThumbProps) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src])

  if (!src || failed) {
    return <YmPhotoUnavailable size={size} hint={ymPhotoUnavailableHint(src ? uploadState : null)} />
  }
  if (zoom) {
    return (
      <ImagePreviewThumb
        src={src}
        alt={alt}
        size={size}
        thumbnailWidth={thumbnailWidth}
        errorFallback={<YmPhotoUnavailable size={size} hint={ymPhotoUnavailableHint(uploadState)} />}
      />
    )
  }
  return (
    <AuthorizedMediaImage
      src={src}
      alt={alt}
      thumbnailWidth={thumbnailWidth}
      className={`${size} shrink-0 rounded bg-gray-100 object-cover dark:bg-zinc-800`}
      onError={() => {
        setFailed(true)
        onLoadError?.()
      }}
      errorFallback={<YmPhotoUnavailable size={size} hint={ymPhotoUnavailableHint(uploadState)} />}
    />
  )
}

export default YmPhotoThumb
